import FolderOutlined from "@mui/icons-material/FolderOutlined";
import PermMediaOutlined from "@mui/icons-material/PermMediaOutlined";
import { Box, Button, Skeleton, Tooltip, Typography } from "@mui/material";
import { RichTreeView, TreeItem } from "@mui/x-tree-view";
import { useMemo } from "react";
import { useSupabaseCallback } from "../../../server/supabase/hooks";
import { SupabaseFolderService } from "../server/folder-service";
import {
  ROOT_ID,
  filterTreeItems,
  folderParentMap,
  getDescendantFolderIds,
  transformFoldersToTreeItems,
} from "../utils";
import { treeItemSlots } from "./tree-item-slot";

import type { UUID } from "../../../utils/uuid";
import type { Folder } from "../server/folder-service";
import type { FolderId, UiFolderId } from "../utils";
import type { TreeItemProps } from "@mui/x-tree-view";
import type { FC, ReactElement } from "react";

type FolderResult = { success: true; folder: Folder } | { success: false; error?: string };

export const useUpdateFolderParentCallback = (): ((
  folderId: UUID,
  parentFolderId: UUID | null,
) => Promise<FolderResult | undefined>) =>
  useSupabaseCallback(async ({ supabase }, folderId: UUID, parentFolderId: UUID | null) => {
    const result = await new SupabaseFolderService(supabase).update(folderId, {
      parent_folder_id: parentFolderId,
    });
    if (!result || result?.error) {
      return {
        success: false,
        error: result?.error?.message ?? "Failed to move folder",
      };
    }
    return { success: true, folder: result.data };
  }, []);

interface FolderParentPickerProps {
  folderId: UUID;
  selectedParentFolderId: UiFolderId;
  onSelectParentFolder: (parentFolderId: UiFolderId) => void;
  folders: Folder[] | null;
  loading: boolean;
  error: Error | null;
}

export const FolderParentPicker: FC<FolderParentPickerProps> = ({
  folderId,
  selectedParentFolderId,
  onSelectParentFolder,
  folders,
  loading,
  error,
}) => {
  const currentFolder = useMemo(
    () => folders?.find((f) => f.id === folderId) ?? null,
    [folders, folderId],
  );

  const currentParentFolderId = useMemo(
    () => currentFolder?.parent_folder_id ?? ROOT_ID,
    [currentFolder],
  );

  const folderMap = useMemo(() => folderParentMap(folders), [folders]);

  // Get IDs of the folder being moved and all its descendants (can't move a folder into itself or its children)
  const excludedFolderIds = useMemo(() => {
    const excluded = new Set<string>([folderId]);
    if (folders) {
      const descendants = getDescendantFolderIds(folderId, folders);
      for (const id of descendants) {
        excluded.add(id);
      }
    }
    return excluded;
  }, [folderId, folders]);

  // Filter the tree to exclude the folder being moved and its descendants
  const filteredTreeItems = useMemo(() => {
    const allItems = transformFoldersToTreeItems(folders ?? []);
    return filterTreeItems(allItems, excludedFolderIds);
  }, [folders, excludedFolderIds]);

  const expandedItems = useMemo(() => {
    const expanded = [ROOT_ID];
    if (!selectedParentFolderId || selectedParentFolderId === ROOT_ID) return expanded;

    let currentId: FolderId = selectedParentFolderId;
    while (currentId) {
      const parentId = folderMap.get(currentId);
      if (!parentId) break;
      expanded.push(parentId);
      currentId = parentId;
    }

    return expanded;
  }, [folderMap, selectedParentFolderId]);

  const hasPendingMove = selectedParentFolderId !== currentParentFolderId;

  if (loading) {
    return (
      <Box px={1} py={0.5}>
        <Skeleton variant="text" width={200} />
        <Skeleton variant="text" width={180} />
        <Skeleton variant="text" width={160} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box px={1} py={0.5}>
        <Typography variant="body2" color="error">
          Unable to load folders
        </Typography>
      </Box>
    );
  }

  return (
    <RichTreeView
      items={filteredTreeItems}
      itemChildrenIndentation={8}
      selectedItems={selectedParentFolderId}
      defaultExpandedItems={expandedItems}
      onItemClick={(_, itemId) => {
        // oxlint-disable-next-line typescript/no-unsafe-type-assertion
        onSelectParentFolder(itemId === ROOT_ID ? itemId : (itemId as UUID));
      }}
      {...treeItemSlots(FolderParentTreeItem, { hasPendingMove, selectedParentFolderId })}
      expansionTrigger="iconContainer"
      sx={{
        "& .MuiTreeItem-content": {
          pr: 1,
          pl: "calc(8px + var(--TreeView-itemChildrenIndentation) * var(--TreeView-itemDepth))",
          py: 0.5,
          borderRadius: 1,
        },
        "& .MuiTreeItem-label": {
          width: "100%",
        },
        "& .MuiTreeItem-group": {
          ml: 2,
        },
      }}
    />
  );
};

const FolderParentTreeItem = (
  props: TreeItemProps & {
    hasPendingMove: boolean;
    onConfirmMove?: () => Promise<void>;
    selectedParentFolderId: FolderId;
    moving?: boolean;
  },
): ReactElement => {
  const {
    itemId,
    label,
    hasPendingMove,
    onConfirmMove,
    selectedParentFolderId,
    moving = false,
    ...rest
  } = props;
  const labelText = typeof label === "string" ? label : "";
  const isRoot = itemId === ROOT_ID;
  const isSelected = itemId === selectedParentFolderId;
  const showMoveButton = isSelected && hasPendingMove && onConfirmMove;

  return (
    <TreeItem
      {...rest}
      itemId={itemId}
      label={
        <Box display="flex" alignItems="center" gap={1} width="100%">
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            flexGrow={1}
            data-testid={`Folder picker item ${labelText}`}
          >
            {isRoot ? <PermMediaOutlined fontSize="small" /> : <FolderOutlined fontSize="small" />}
            <Typography variant="body2" noWrap title={labelText} sx={{ minWidth: 0 }}>
              {label}
            </Typography>
          </Box>
          {showMoveButton && (
            <Tooltip title="Move folder here">
              <span>
                <Button
                  disabled={moving}
                  size="small"
                  variant="text"
                  onClick={(event) => {
                    event.stopPropagation();
                    void onConfirmMove();
                  }}
                  data-analytics-id="folder-parent-move-confirm"
                >
                  Move here
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      }
    />
  );
};
